import { ExpressionValue } from "./ExpressionValue";
import { IBaseExpressionElement } from "./IBaseExpressionElement";
import { ExpressionConfiguration } from "./ExpressionConfiguration";
import { IDataDefinition } from "../Common/Data/Definitions/IDataDefinition";
import { StateManager } from "../Common/StateManager";

export class ExpressionContext {
    emptyExpressionText: string | null = null;
    definition?: ExpressionConfiguration;
    onSelection?: (selectedId: string) => void;
    expression: ExpressionConfiguration;
    requiredVariable?: IDataDefinition;
    // events
    onExpressionUpdate?: () => void;
    private ownership: {
        [id: string]: {
            owned: IBaseExpressionElement;
            owner: IBaseExpressionElement;
        };
    } = {};
    private selected?: string;

    constructor(expression: ExpressionConfiguration) {
        this.expression = expression;
    }

    clearSelection(): void {
        this.selected = undefined;
        StateManager.propertyChanged(this, "selected");
    }

    setSelection(id: string): void {
        this.selected = id;
        StateManager.propertyChanged(this, "selected");
        if (this.onSelection) {
            this.onSelection(this.selected);
        }
    }

    getSelection(): string | null | undefined {
        return this.selected;
    }

    register(owned: IBaseExpressionElement, owner: IBaseExpressionElement): void {
        if (owned._id === owner._id) throw new Error("cannot set owner as owned");

        this.ownership[owned._id] = { owned: owned, owner: owner };
    }

    getParent(id: string): IBaseExpressionElement | null {
        return this.ownership[id]?.owner;
    }

    getParents(id: string): IBaseExpressionElement[] {
        let parents: IBaseExpressionElement[] = [];

        let parent = this.getParent(id);

        if (parent) {
            let type = Reflect.get(parent, "__type");
            if (type === "__RootExpression") {
                return parents;
            }
            parents.push(parent);
            return [...parents, ...this.getParents(parent._id)];
        } else {
            return parents;
        }
    }

    getElement(id: string): IBaseExpressionElement | null {
        return this.ownership[id]?.owned;
    }

    canSelectParent(id: string): boolean {
        let parent = this.getParent(id);

        while (parent != null) {
            if (parent.canSelect) return true;

            parent = this.getParent(parent._id);
        }

        return false;
    }

    selectParent(id: string) {
        let parent = this.getParent(id);

        while (parent != null) {
            if (parent.canSelect) {
                this.setSelection(parent._id);
            }

            parent = this.getParent(parent._id);
        }
    }

    refresh() {
        StateManager.changed(this);

        if (this.onExpressionUpdate) {
            this.onExpressionUpdate();
        }
    }

    getParentExpressionValue(id: string): ExpressionValue | null {
        let parent = this.ownership[id];

        if (!parent) {
            // no parent
            return null;
        }

        if (ExpressionValue.isExpressionValue(parent.owner)) {
            return parent.owner;
        }

        return this.getParentExpressionValue(parent.owner._id);
    }

    checkVariableUsed(variable: IDataDefinition): boolean {
        return !!Object.values(this.ownership).find((o) => {
            if (o.owned instanceof ExpressionValue && o.owned.variableName === variable.name) {
                return true;
            }
            if (o.owner instanceof ExpressionValue && o.owner.variableName === variable.name) {
                return true;
            }

            return false;
        });
    }
}
